import React, { useRef, useState } from 'react';
import { MIL } from '../content/narrative';
import { sans, mono, threadShell, ThreadBrief } from './ThreadParts';

/* MIL, shown as two Threads — never a chat window. Planning: the walk's next
   team, every carried-in lesson traced to its source. Diagnosing: a Rev B
   field report where MIL's read is a proposal awaiting review and doesn't
   reopen a problem the record shows is fixed. Breadcrumbs place each Thread
   in its product tree. */

const ACTOR = {
  MIL: { color: 'var(--wb-orange)', border: 'var(--wb-orange)' },
  Engine: { color: 'var(--wb-ink)', border: 'var(--wb-ink)' },
  You: { color: 'var(--wb-on-ink)', background: 'var(--wb-ink)', border: 'var(--wb-ink)' },
};

const Crumbs = ({ crumbs, title }) => (
  <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--wb-line)', background: 'var(--wb-soft)' }}>
    <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)' }}>
      {crumbs.map((c, n) => (
        <React.Fragment key={c}>{n > 0 && <span aria-hidden> › </span>}<span>{c}</span></React.Fragment>
      ))}
    </div>
    <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--wb-orange)', marginTop: 2 }}>
      Thread · <span style={{ color: 'var(--wb-ink)' }}>{title}</span>
    </div>
  </div>
);

const Label = ({ children }) => (
  <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>{children}</div>
);

const RowList = ({ rows }) => (
  <div style={{ display: 'grid', gap: 6 }}>
    {rows.map(([k, v]) => (
      <div key={k} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3" style={{ background: 'var(--wb-soft)', borderRadius: 10, padding: '8px 12px' }}>
        <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', flex: '0 0 auto', minWidth: 120 }}>{k}</span>
        <span style={{ fontFamily: sans, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', lineHeight: 1.5 }}>{v}</span>
      </div>
    ))}
  </div>
);

const Planning = ({ t }) => (
  <>
    <ThreadBrief rows={t.brief} />
    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--wb-line)' }}>
      <Label>{t.traceLabel}</Label>
      <div style={{ display: 'grid', gap: 6 }}>
        {t.trace.map(([id, what, from]) => (
          <div key={id} style={{ fontFamily: sans, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', lineHeight: 1.5 }}>
            <span style={{ fontFamily: mono, fontWeight: 800 }}>{id}</span> {what}{' '}
            <span style={{ fontFamily: mono, color: 'var(--wb-muted)' }}>← {from}</span>
          </div>
        ))}
      </div>
    </div>
    <div style={{ padding: '14px 18px' }}>
      <div className="flex items-center justify-between gap-2"><Label>{t.planLabel}</Label>
        <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', border: '1px solid var(--wb-orange)', borderRadius: 999, padding: '0 8px', marginBottom: 8 }}>Proposed</span></div>
      <ol className="list-none p-0 m-0" style={{ display: 'grid', gap: 4 }}>
        {t.plan.map(([step, who], i) => {
          const a = ACTOR[who.split(' ')[0]];
          return (
            <li key={step} className="flex items-baseline justify-between gap-3" style={{ padding: '5px 0', borderBottom: '1px dashed var(--wb-line)' }}>
              <span style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-ink)', lineHeight: 1.4 }}>
                <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', marginRight: 8 }}>{String(i + 1).padStart(2, '0')}</span>{step}
              </span>
              <span style={{ flex: '0 0 auto', fontFamily: mono, fontSize: 'var(--t-sm)', fontWeight: 700, padding: '1px 8px', borderRadius: 999, border: `1px solid ${a.border}`, color: a.color, background: a.background || 'transparent' }}>{who}</span>
            </li>
          );
        })}
      </ol>
      <div style={{ marginTop: 12 }}><span className="wb-btn" aria-hidden style={{ pointerEvents: 'none', display: 'inline-flex' }}>{t.action}</span></div>
    </div>
  </>
);

const Diagnosing = ({ t }) => {
  const [open, setOpen] = useState(t.groups[0].id);
  return (
    <>
      <ThreadBrief rows={t.brief} />
      <div style={{ padding: '14px 18px' }}>
        <Label>{t.proposalLabel}</Label>
        <p style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-ink)', margin: 0, lineHeight: 1.55, borderLeft: '2px solid var(--wb-orange)', paddingLeft: 12 }}>{t.proposal}</p>
      </div>
      {t.groups.map((g) => {
        const isOpen = open === g.id;
        return (
          <div key={g.id} style={{ borderTop: '1px solid var(--wb-line)' }}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : g.id)}
              aria-expanded={isOpen}
              aria-controls={`diag-${g.id}`}
              className="w-full flex items-center justify-between gap-3 text-left"
              style={{ padding: '12px 18px', background: isOpen ? 'rgba(185,79,30,0.05)' : 'none', border: 0, cursor: 'pointer', fontFamily: sans, fontSize: 'var(--t-md)', fontWeight: 700, color: 'var(--wb-ink)' }}
            >
              <span>{g.label}</span>
              <span aria-hidden style={{ fontFamily: mono, color: 'var(--wb-orange)' }}>{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && <div id={`diag-${g.id}`} style={{ padding: '0 18px 16px' }}><RowList rows={g.rows} /></div>}
          </div>
        );
      })}
    </>
  );
};

const MilTabs = () => {
  const [active, setActive] = useState(0);
  const refs = useRef([]);
  const onKey = (e) => {
    const last = MIL.tabs.length - 1;
    let n = null;
    if (e.key === 'ArrowRight') n = active === last ? 0 : active + 1;
    else if (e.key === 'ArrowLeft') n = active === 0 ? last : active - 1;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = last;
    if (n === null) return;
    e.preventDefault();
    setActive(n);
    refs.current[n]?.focus();
  };
  const t = MIL.tabs[active];
  return (
    <div>
      <div role="tablist" aria-label="MIL Threads" onKeyDown={onKey} className="flex gap-2" style={{ marginBottom: 10 }}>
        {MIL.tabs.map((tab, i) => {
          const on = i === active;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`mil-tab-${tab.id}`}
              aria-selected={on}
              aria-controls="mil-panel"
              tabIndex={on ? 0 : -1}
              ref={(el) => { refs.current[i] = el; }}
              onClick={() => setActive(i)}
              style={{
                fontFamily: mono, fontSize: 'var(--t-sm)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                color: on ? 'var(--wb-on-ink)' : 'var(--wb-ink)', background: on ? 'var(--wb-orange)' : 'var(--wb-paper)',
                border: `1px solid ${on ? 'var(--wb-orange)' : 'var(--wb-line)'}`,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id="mil-panel" aria-labelledby={`mil-tab-${t.id}`} tabIndex={0} style={threadShell}>
        <Crumbs crumbs={t.crumbs} title={t.title} />
        {t.id === 'planning' ? <Planning t={t} /> : <Diagnosing t={t} key={t.id} />}
      </div>
    </div>
  );
};

export default MilTabs;
